import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { InlineChevronRight } from "@acto/web-common/components";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import {
  MeetingRowInfo,
  MeetingRowName,
  MeetingSectionLabel,
  MeetingTimeBox,
} from "@acto/taskpane/field-sales-2/components/primitives/MeetingRow";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { formatHourMinute } from "@acto/taskpane/field-sales-2/utils/format";
import { mapsSearchUrl } from "@acto/taskpane/field-sales-2/utils/maps";

const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[16],
}));

/**
 * The map link sits in the address slot, so the row holds two controls and cannot be the
 * button a list row is. `:hover` and `:active` still resolve from a descendant, which is what
 * lets a plain container light up the way a list row does.
 */
const Row = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: theme.fieldSalesTwo.spacing[8],
  margin: `-${theme.fieldSalesTwo.spacing[8]}`,
  borderRadius: theme.fieldSalesTwo.radius[20],
  transition: "background-color 120ms ease, transform 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
  ...pressInteraction(theme),
}));

/** Stretches over the row, so every part of it the map link does not cover opens the customer. */
const CustomerButton = styled("button")({
  display: "block",
  padding: 0,
  border: "none",
  background: "transparent",
  textAlign: "left",
  font: "inherit",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
  },
});

const MapsLink = styled("a")(({ theme }) => ({
  // Above the customer button's stretched hit area, which would otherwise swallow the tap.
  position: "relative",
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.accent.action,
  textDecoration: "none",
  WebkitTapHighlightColor: "transparent",
}));

interface NextMeetingCardProps {
  meeting: Meeting;
  onOpenCustomer: (customerId: string) => void;
  /** The link navigates on its own; this only observes the tap. */
  onOpenMaps?: () => void;
}

export function NextMeetingCard({ meeting, onOpenCustomer, onOpenMaps }: NextMeetingCardProps) {
  const t = useTranslations("fieldSalesTwo.home.calendar");
  const mapsUrl = mapsSearchUrl(meeting.customer);

  return (
    <Section>
      <MeetingSectionLabel>{t("nextMeeting")}</MeetingSectionLabel>
      <Row>
        <MeetingTimeBox>
          {meeting.scheduledStartTime ? formatHourMinute(meeting.scheduledStartTime) : ""}
        </MeetingTimeBox>
        <MeetingRowInfo>
          <CustomerButton type="button" onClick={() => onOpenCustomer(meeting.customer.id)}>
            <MeetingRowName>{meeting.customer.name}</MeetingRowName>
          </CustomerButton>
          {mapsUrl && (
            <MapsLink href={mapsUrl} target="_blank" rel="noreferrer" onClick={onOpenMaps}>
              {t("openInMaps")}
              <InlineChevronRight aria-hidden="true" />
            </MapsLink>
          )}
        </MeetingRowInfo>
      </Row>
    </Section>
  );
}
